<template>
  <ContextMenuRoot>
    <ContextMenuTrigger as-child>
      <div
        class="rounded-[6px] overflow-hidden group w-[200px] flex-[1_1_200px] md:max-w-[240px]"
      >
        <div
          :class="[
            'h-[170px] bg-[#1e2121] relative after:content-[\'*\'] after:transition after:opacity-0 after:bg-black/60 after:absolute after:top-0 after:right-0 after:bottom-0 after:left-0 group-hover:after:opacity-100',
            { 'after:opacity-100': dropdownMenuOpen },
          ]"
        >
          <div
            v-if="isUploadCoverPending || isRemoveCoverPending"
            class="h-full w-full flex justify-center items-center"
          >
            <SvgIcon :width="'40'" :height="'40'" icon="app-icons/spinner" />
          </div>
          <template v-else>
            <img
              v-if="project.cover_url"
              :src="project.cover_url"
              class="h-full w-full object-cover"
            />
            <div v-else class="h-full w-full flex justify-center items-center">
              <SvgIcon
                :width="'40'"
                :height="'40'"
                :fill="'#394141'"
                icon="app-icons/project"
              />
            </div>
          </template>
          <router-link
            v-if="!isEditingName"
            :to="{
              name: 'project',
              params: { hash: project.url_hash },
              query: { entrypoint: 'app' },
            }"
            class="absolute top-0 left-0 bottom-0 right-0 z-[1]"
          />
          <Protect
            :permissions="[
              SYS_PERMISSIONS.SYS_PROJECTS_DELETE,
              SYS_PERMISSIONS.SYS_PROJECTS_DUPLICATE,
              SYS_PERMISSIONS.SYS_PROJECTS_EDIT,
            ]"
            :skip-protect="project.is_project_owner"
          >
            <DropdownMenu v-model:open="dropdownMenuOpen" :modal="false">
              <DropdownMenuTrigger
                :class="[
                  'absolute top-0 right-0 z-10 transition opacity-0 group-hover:opacity-100',
                  { 'opacity-100': dropdownMenuOpen },
                ]"
                as-child
              >
                <IconButton type="button" variant="ghost">
                  <MoreHorizontal class="size-6" />
                </IconButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent :align="'center'">
                <Protect
                  :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_DELETE]"
                  :skip-protect="project.is_project_owner"
                >
                  <DropdownMenuItem @click="deleteProject">
                    Delete
                  </DropdownMenuItem>
                </Protect>
                <Protect
                  :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_DUPLICATE]"
                  :skip-protect="project.is_project_owner"
                >
                  <DropdownMenuItem
                    v-if="project.status === 'active'"
                    @click="duplicateProject"
                  >
                    <div class="flex justify-between w-full">
                      <span>Duplicate</span>
                      <Badge v-if="!isDuplicateSupportedFeature">PRO</Badge>
                    </div>
                  </DropdownMenuItem>
                </Protect>
                <Protect
                  :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
                  :skip-protect="project.is_project_owner"
                >
                  <DropdownMenuItem
                    v-if="project.status === 'active'"
                    @click="renameProject"
                  >
                    Rename
                  </DropdownMenuItem>
                </Protect>
                <Protect
                  :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
                  :skip-protect="project.is_project_owner"
                >
                  <DropdownMenuItem
                    v-if="project.status === 'active'"
                    as="label"
                  >
                    <span v-if="project.cover_url">Change cover</span>
                    <span v-else>Upload cover</span>
                    <input
                      :id="'thumb-' + project.id"
                      ref="projectFileUpload"
                      class="hidden"
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      @change="uploadCover"
                    />
                  </DropdownMenuItem>
                </Protect>
                <Protect
                  :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
                  :skip-protect="project.is_project_owner"
                >
                  <DropdownMenuItem
                    v-if="project.status === 'active' && project.cover_url"
                    @click="removeCover"
                  >
                    Remove cover
                  </DropdownMenuItem>
                </Protect>
              </DropdownMenuContent>
            </DropdownMenu>
          </Protect>
        </div>
        <div class="p-2.5 bg-[#394141] flex relative">
          <div class="w-full">
            <input
              v-if="isEditingName"
              ref="nameInputRef"
              :value="project.name"
              class="text-sm text-orange-600 bg-transparent border-b border-orange-600 border-dashed outline-none mb-[3px]"
              @blur="updateProjectName"
              @keyup.enter="$event.target.blur()"
            />
            <Tooltip v-else side="top" :side-offset="8" :delay-duration="400">
              <router-link
                :to="{ name: 'project', params: { hash: project.url_hash } }"
                class="text-sm text-ellipsis overflow-hidden whitespace-nowrap text-white hover:text-white relative z-[2] inline-block leading-[1.2] max-w-full pt-[2px]"
              >
                {{ project.name }}
              </router-link>
              <template #content>
                <p>{{ project.name }}</p>
              </template>
            </Tooltip>
            <div class="text-xs text-white/50">
              Modified {{ project.date_format }}
            </div>
          </div>
          <router-link
            v-if="!isEditingName"
            :to="{ name: 'project', params: { hash: project.url_hash } }"
            class="absolute top-0 left-0 bottom-0 right-0 z-[1]"
          />
        </div>
      </div>
    </ContextMenuTrigger>
    <Protect
      :permissions="[
        SYS_PERMISSIONS.SYS_PROJECTS_DELETE,
        SYS_PERMISSIONS.SYS_PROJECTS_DUPLICATE,
        SYS_PERMISSIONS.SYS_PROJECTS_EDIT,
      ]"
      :skip-protect="project.is_project_owner"
    >
      <ContextMenuPortal>
        <ContextMenuContent
          class="min-w-[195px] z-30 bg-neutral-900 p-1 text-neutral-300 outline-none rounded-md will-change-[opacity,transform] data-[side=top]:animate-slideDownAndFade data-[side=right]:animate-slideLeftAndFade data-[side=bottom]:animate-slideUpAndFade data-[side=left]:animate-slideRightAndFade"
        >
          <Protect
            :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_DELETE]"
            :skip-protect="project.is_project_owner"
          >
            <ContextMenuItem
              value="Delete"
              class="group text-sm leading-none rounded-sm flex items-center px-2 py-1.5 select-none outline-none cursor-pointer hover:bg-neutral-50/[.12] focus:bg-neutral-50/[.12] transition-colors duration-200 ease-in-out"
              @click="deleteProject"
            >
              Delete
            </ContextMenuItem>
          </Protect>
          <Protect
            :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_DUPLICATE]"
            :skip-protect="project.is_project_owner"
          >
            <ContextMenuItem
              v-if="project.status === 'active'"
              value="Duplicate"
              class="group text-sm leading-none rounded-sm flex items-center px-2 py-1.5 select-none outline-none cursor-pointer hover:bg-neutral-50/[.12] focus:bg-neutral-50/[.12] transition-colors duration-200 ease-in-out"
              @click="duplicateProject"
            >
              <div class="flex justify-between w-full">
                <span>Duplicate</span>
                <Badge v-if="!isDuplicateSupportedFeature">PRO</Badge>
              </div>
            </ContextMenuItem>
          </Protect>
          <Protect
            :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
            :skip-protect="project.is_project_owner"
          >
            <ContextMenuItem
              v-if="project.status === 'active'"
              value="Rename"
              class="group text-sm leading-none rounded-sm flex items-center px-2 py-1.5 select-none outline-none cursor-pointer hover:bg-neutral-50/[.12] focus:bg-neutral-50/[.12] transition-colors duration-200 ease-in-out"
              @click="renameProject"
            >
              Rename
            </ContextMenuItem>
          </Protect>
          <Protect
            :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
            :skip-protect="project.is_project_owner"
          >
            <template v-if="project.status === 'active'">
              <ContextMenuItem
                :value="project.cover_url ? 'Change cover' : 'Upload cover'"
                class="group text-sm leading-none rounded-sm flex items-center px-2 py-1.5 select-none outline-none cursor-pointer hover:bg-neutral-50/[.12] focus:bg-neutral-50/[.12] transition-colors duration-200 ease-in-out"
                as="label"
                :for="'thumb-' + project.id"
                @click="() => $refs.projectFileUpload.click()"
              >
                <span v-if="project.cover_url">Change cover</span>
                <span v-else>Upload cover</span>
              </ContextMenuItem>
              <input
                :id="'thumb-' + project.id"
                ref="projectFileUpload"
                class="hidden"
                type="file"
                accept="image/jpeg, image/jpg, image/png"
                @change="uploadCover"
              />
            </template>
          </Protect>
          <Protect
            :permissions="[SYS_PERMISSIONS.SYS_PROJECTS_EDIT]"
            :skip-protect="project.is_project_owner"
          >
            <template v-if="project.status === 'active' && project.cover_url">
              <ContextMenuItem
                value="Remove cover"
                class="group text-sm leading-none rounded-sm flex items-center px-2 py-1.5 select-none outline-none cursor-pointer hover:bg-neutral-50/[.12] focus:bg-neutral-50/[.12] transition-colors duration-200 ease-in-out"
                @click="removeCover"
              >
                Remove cover
              </ContextMenuItem>
            </template></Protect
          >
        </ContextMenuContent>
      </ContextMenuPortal>
    </Protect>
  </ContextMenuRoot>
  <!-- Delete Dialog -->
  <AlertDialog v-model:open="deleteDialogOpen">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          Delete
          <strong>{{ project.name }}</strong>
          ?
        </AlertDialogTitle>
      </AlertDialogHeader>
      <div class="px-7 space-y-4">
        <AlertDialogDescription>
          Are you sure you want to delete
          <strong>{{ project.name }}</strong> project?
        </AlertDialogDescription>
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel as-child>
          <Button variant="ghost" :disabled="isDeletePending">Cancel</Button>
        </AlertDialogCancel>
        <Button
          variant="danger"
          :disabled="isDeletePending"
          :loading="isDeletePending"
          @click="confirmDelete"
        >
          Delete
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
  <!-- Duplicate Dialog -->
  <UpgradeModal v-model:open="duplicatePremiumDialogOpen">
    <template #upgradeMessage>
      <Alert
        title="The duplicate project feature is not available to your current plan"
        subtitle="Upgrade to any of our plans to unlock duplicate project feature"
        class="mb-6"
      />
    </template>
  </UpgradeModal>
</template>

<script setup>
import { ref, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { MoreHorizontal } from 'lucide-vue-next';
import { Alert } from '@ui/components/alert';
import { Tooltip } from '@ui/components/tooltip';

// TODO: create a wrapper in our @ui/components lib
import {
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuPortal,
  ContextMenuRoot,
  ContextMenuTrigger,
} from 'radix-vue';
import { onClickOutside } from '@vueuse/core';
import { toast } from 'vue-sonner';
import { SYS_PERMISSIONS } from '@/constants.js';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ui/components/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@ui/components/dropdown-menu';
import { Badge } from '@ui/components/badge';
import { Button } from '@ui/components/button';
import { IconButton } from '@ui/components/icon-button';
import SvgIcon from '@/components/all/SvgIcon.vue';
import requests from '@vuex/requests.js';

import { UpgradeModal } from '@components/common/UpgradeModal';
import { usePremiumFeature } from '@composables/usePremiumFeature.js';
import Protect from '@/hoc/Protect.vue';
import { queries } from '@/queries/index.js';

const queryClient = useQueryClient();
const store = useStore();
const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
  totalCount: {
    type: Number,
    required: true,
  },
});

const dropdownMenuOpen = ref(false);
const deleteDialogOpen = ref(false);
const duplicatePremiumDialogOpen = ref(false);
const isEditingName = ref(false);
const nameInputRef = ref(null);

const { isSupported: isDuplicateSupportedFeature } = usePremiumFeature({
  feature: 'duplicate_project',
});
const { isSupported: isProjectLimitSupportedFeature } = usePremiumFeature({
  feature: 'projects',
  limit: props.totalCount,
});

// METHODS
// Rename project
const renameProject = async () => {
  dropdownMenuOpen.value = false;
  if (!dropdownMenuOpen.value) {
    await nextTick();
    isEditingName.value = true;
    await nextTick();
    nameInputRef.value.focus();
  }
};

const updateProjectName = async () => {
  let name = nameInputRef.value.value;
  if (name === props.project.name) {
    isEditingName.value = false;
    return;
  }
  if (name === '') {
    name = 'Untitled';
  }
  await store.dispatch('user/projects/updateName', {
    hash: props.project.url_hash,
    name,
  });
  await queryClient.invalidateQueries({
    queryKey: queries.workspace.projects.queryKey,
  });
  isEditingName.value = false;
};

onClickOutside(nameInputRef, () => {
  if (isEditingName.value) {
    isEditingName.value = false;
    updateProjectName();
  }
});

// Delete project
const deleteProject = async () => {
  deleteDialogOpen.value = true;
};

const { isPending: isDeletePending, mutate: deleteProjectMutation } =
  useMutation({
    mutationFn: async payload => {
      await requests.projects.deleteProject(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queries.workspace.projects.queryKey,
      });
    },
    onError: async error => {
      const { title: message } = error.response.data;
      toast.error(message || 'Something went wrong, please try again later');
      deleteDialogOpen.value = false;
    },
  });

const confirmDelete = async () => {
  await store.dispatch('application/channels/leaveProjectChannels', {
    hash: props.project.url_hash,
  });
  await deleteProjectMutation({ hash: props.project.url_hash });
};

// Duplicate project
const { mutate: duplicateProjectMutation } = useMutation({
  mutationFn: async payload => {
    await requests.projects.duplicateProject(payload);
  },
  onSuccess: async () => {
    await queryClient.invalidateQueries({
      queryKey: queries.workspace.projects.queryKey,
    });
  },
  onError: async error => {
    const { title: message } = error.response.data;
    toast.error(message || 'Something went wrong, please try again later');
  },
});

const duplicateProject = () => {
  // Check if duplicate is supported
  if (!isDuplicateSupportedFeature.value) {
    duplicatePremiumDialogOpen.value = true;
    return;
  }
  // Check if project limit is supported and if it is, duplicate the project
  // otherwise, show upgrade dialog
  if (isProjectLimitSupportedFeature.value) {
    duplicateProjectMutation({ hash: props.project.url_hash });
  }
};

// Remove cover
const { isPending: isRemoveCoverPending, mutate: removeCoverMutation } =
  useMutation({
    mutationFn: async payload => {
      await requests.assets.removeCover(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queries.workspace.projects.queryKey,
      });
    },
  });

const removeCover = () => {
  removeCoverMutation({
    hash: props.project.url_hash,
    file: props.project.cover_type.file,
    type: props.project.cover_type.type,
  });
};

// Upload cover
const { isPending: isUploadCoverPending, mutate: uploadCoverMutation } =
  useMutation({
    mutationFn: async payload => {
      await requests.assets.uploadCover(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queries.workspace.projects.queryKey,
      });
    },
  });

const uploadCover = async event => {
  const file = event.target.files[0];
  console.log(file);

  const reader = new FileReader();
  let imageUrl = '';
  reader.onload = e => {
    imageUrl = e.target.result;
    console.log(imageUrl);

    uploadCoverMutation({
      data: imageUrl,
      hash: props.project.url_hash,
    });
  };
  reader.readAsDataURL(file);
};
</script>
